import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { submitSuggestion, resetGame, logout } from '../utils/api';
import { ArrowLeft, Volume2, VolumeX, MessageSquare, RotateCcw, LogOut } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export default function Settings({ user, setUser, refreshUser }) {
  const navigate = useNavigate();
  const [musicEnabled, setMusicEnabled] = useState(() => {
    const stored = localStorage.getItem('music_enabled');
    return stored === null || stored === 'true';
  });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [suggestion, setSuggestion] = useState('');
  const [loading, setLoading] = useState(false);

  const handleMusicToggle = (checked) => {
    setMusicEnabled(checked);
    localStorage.setItem('music_enabled', checked.toString());
    
    // Dispatch event to control AudioPlayer
    window.dispatchEvent(new CustomEvent('musicToggle', { 
      detail: { enabled: checked } 
    }));
    
    toast.success(checked ? 'Music enabled' : 'Music disabled');
  };

  const handleSubmitSuggestion = async () => {
    if (!suggestion.trim()) {
      toast.error('Please enter a suggestion');
      return;
    }

    setLoading(true);
    try {
      await submitSuggestion(suggestion);
      toast.success('Suggestion submitted! Thank you.');
      setSuggestion('');
    } catch (error) {
      toast.error('Failed to submit suggestion');
    } finally {
      setLoading(false);
    }
  };

  const handleResetGame = async () => {
    if (!user) {
      toast.error('Please sign in first');
      return;
    }

    setLoading(true);
    try {
      console.log('Calling resetGame API...');
      const response = await resetGame();  // Defaults to false - permanently deletes everything
      console.log('Reset game response:', response);
      
      // Refresh user data from backend to get the reset state
      if (refreshUser) {
        console.log('Refreshing user data...');
        await refreshUser();
        console.log('User data refreshed');
      }
      
      toast.success('Game reset successfully! All progress, scores, and members permanently deleted.');
      navigate('/level-select');
    } catch (error) {
      console.error('Reset game error:', error);
      toast.error(`Failed to reset game: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('session_token');
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  return (
    <div 
      className="min-h-screen py-6 sm:py-8 md:py-12 px-3 sm:px-4 md:px-6 relative"
      data-testid="settings-page"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      <div className="max-w-3xl mx-auto relative z-10">
        <Button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-white hover:bg-white/20"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/9qio0cok_Mega%20Church%20Logo%20T%20Shirt.png"
              alt="Mega Church Logo"
              className="w-20 h-20 object-contain"
            />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="settings-title">Settings</h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90">Customize your game experience</p>
        </div>

        {/* Audio Settings */}
        <Card className="bg-white/95 backdrop-blur-lg rounded-3xl p-8 mb-6">
          <h2 className="text-2xl font-bold text-[#4169E1] mb-6">Audio Settings</h2>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {musicEnabled ? <Volume2 className="w-6 h-6 text-[#4169E1]" /> : <VolumeX className="w-6 h-6 text-gray-400" />}
                <Label htmlFor="music" className="text-lg font-semibold text-gray-700">Background Music</Label>
              </div>
              <Switch
                id="music"
                data-testid="music-toggle"
                checked={musicEnabled}
                onCheckedChange={handleMusicToggle}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {soundEnabled ? <Volume2 className="w-6 h-6 text-[#4169E1]" /> : <VolumeX className="w-6 h-6 text-gray-400" />}
                <Label htmlFor="sound" className="text-lg font-semibold text-gray-700">Sound Effects</Label>
              </div>
              <Switch
                id="sound"
                data-testid="sound-toggle"
                checked={soundEnabled}
                onCheckedChange={setSoundEnabled}
              />
            </div>
          </div>
        </Card>

        {/* Admin Dashboard Access - Only for Admin Users */}
        {user && user.role === 'admin' && (
          <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 backdrop-blur-lg rounded-3xl p-6 sm:p-8 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-2xl">👑</span>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-red-600">Admin Dashboard</h2>
                <p className="text-sm text-gray-600">Manage users and tournament</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/admin')}
              className="w-full h-12 text-base sm:text-lg font-bold bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl shadow-lg"
            >
              Open Admin Dashboard
            </Button>
          </Card>
        )}

        {/* Game Suggestion */}
        <Card className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-8 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-[#4169E1] mb-4 sm:mb-6 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
            <span>Game Suggestion</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4">Share your ideas for new Bible trivia topics, features, or improvements!</p>
          
          <Textarea
            data-testid="suggestion-textarea"
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            placeholder="Enter your suggestion here..."
            className="min-h-32 mb-4 text-sm sm:text-base"
          />
          
          <Button
            data-testid="submit-suggestion-btn"
            onClick={handleSubmitSuggestion}
            disabled={loading}
            className="w-full h-12 text-base sm:text-lg font-bold bg-[#4169E1] hover:bg-[#3558c7] text-white rounded-xl min-h-[44px]"
          >
            <span className="text-white">Submit Suggestion</span>
          </Button>
        </Card>

        {/* Reset Game */}
        <Card className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-8 mb-6 border-2 border-red-200">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-2xl sm:text-3xl">⚠️</span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-red-600 mb-2 flex items-center gap-2">
                <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6" />
                <span>Game Reset</span>
              </h2>
              <p className="text-sm sm:text-base text-gray-700 font-semibold">
                {user ? "⚠️ Warning: This will permanently delete all your progress and reset your total members to 0." : "⚠️ Warning: This will permanently delete all your game progress."}
              </p>
            </div>
          </div>
          
          {user ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="reset-game-btn"
                  variant="destructive"
                  className="w-full h-12 text-base sm:text-lg font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl min-h-[44px]"
                >
                  <span className="text-white">Reset Game Progress</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white mx-4">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-xl sm:text-2xl">Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription className="text-sm sm:text-base">
                    This action cannot be undone. This will permanently delete your game progress and reset your total members to 0.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-reset-btn" className="min-h-[44px]">
                    <span>Cancel</span>
                  </AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-reset-btn" onClick={handleResetGame} className="min-h-[44px] bg-red-600 hover:bg-red-700">
                    <span>Reset</span>
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button
              onClick={() => navigate('/auth')}
              className="w-full h-12 text-base sm:text-lg font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl min-h-[44px]"
            >
              <span className="text-white">Game Reset</span>
            </Button>
          )}
        </Card>

        {/* Resources Section */}
        <Card className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-8 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-[#4169E1] mb-4 sm:mb-6 flex items-center gap-2">
            <span className="text-2xl">📚</span>
            <span>Resources</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">Explore helpful guides to improve your game performance and deepen your biblical knowledge.</p>
          
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/bible-study-tips')}
              variant="outline"
              className="w-full h-14 text-base sm:text-lg font-semibold border-2 border-blue-500 text-blue-600 hover:bg-blue-50 rounded-xl flex items-center justify-between group"
            >
              <span className="flex items-center gap-3">
                <span className="text-2xl">📖</span>
                <span>Bible Study Tips & Methods</span>
              </span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Button>
            
            <Button
              onClick={() => navigate('/game-strategies')}
              variant="outline"
              className="w-full h-14 text-base sm:text-lg font-semibold border-2 border-purple-500 text-purple-600 hover:bg-purple-50 rounded-xl flex items-center justify-between group"
            >
              <span className="flex items-center gap-3">
                <span className="text-2xl">🎮</span>
                <span>Game Strategies & Tips</span>
              </span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Button>
            
            <Button
              onClick={() => navigate('/scripture-references')}
              variant="outline"
              className="w-full h-14 text-base sm:text-lg font-semibold border-2 border-green-500 text-green-600 hover:bg-green-50 rounded-xl flex items-center justify-between group"
            >
              <span className="flex items-center gap-3">
                <span className="text-2xl">📜</span>
                <span>Scripture References & Topics</span>
              </span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Button>
          </div>
        </Card>

        {/* Logout */}
        {user && (
          <Card className="bg-white/95 backdrop-blur-lg rounded-3xl p-8">
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="w-full h-12 text-lg font-bold border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-xl"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
